import { useState, useEffect, createContext, useContext } from 'react'
import { BrowserRouter as Router, Routes, Route, Link, useParams, useNavigate } from 'react-router-dom'

// Butterbase API configuration
const APP_ID = 'app_2rss7rhhhxd9'
const API_BASE = `https://api.butterbase.ai/v1/${APP_ID}`

// Auth Context
const AuthContext = createContext(null)

function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for stored token on mount
    const token = localStorage.getItem('butterbase_token')
    const userData = localStorage.getItem('user_data')
    if (token && userData) {
      setUser(JSON.parse(userData))
    }
    setLoading(false)
  }, [])

  const login = async (email, password) => {
    const response = await fetch(`${API_BASE}/auth/signin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    })
    
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Login failed')
    }
    
    const data = await response.json()
    localStorage.setItem('butterbase_token', data.access_token)
    localStorage.setItem('user_data', JSON.stringify(data.user))
    setUser(data.user)
    return data
  }

  const register = async (email, password) => {
    const response = await fetch(`${API_BASE}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    })
    
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Registration failed')
    }
    
    const data = await response.json()
    localStorage.setItem('butterbase_token', data.access_token)
    localStorage.setItem('user_data', JSON.stringify(data.user))
    setUser(data.user)
    return data
  }

  const logout = () => {
    localStorage.removeItem('butterbase_token')
    localStorage.removeItem('user_data')
    setUser(null)
  }

  const getToken = () => localStorage.getItem('butterbase_token')

  return (
    <AuthContext.Provider value={{ user, login, register, logout, getToken, loading }}>
      {children}
    </AuthContext.Provider>
  )
}

function useAuth() {
  return useContext(AuthContext)
}

// API helpers
async function fetchAPI(endpoint, options = {}) {
  const token = localStorage.getItem('butterbase_token')
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  
  const response = await fetch(`${API_BASE}/${endpoint}`, {
    ...options,
    headers
  })
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }))
    throw new Error(error.message || `HTTP ${response.status}`)
  }
  
  return response.json()
}

// Toast component
function Toast({ message, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000)
    return () => clearTimeout(timer)
  }, [onClose])
  
  return <div className="toast">{message}</div>
}

// Cart Context
const CartContext = createContext(null)

function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([])
  const [loading, setLoading] = useState(true)
  const { user, getToken } = useAuth()

  const fetchCart = async () => {
    if (!user) {
      // Load from localStorage for non-authenticated users
      const saved = localStorage.getItem('cart')
      setCartItems(saved ? JSON.parse(saved) : [])
      setLoading(false)
      return
    }
    
    try {
      const items = await fetchAPI('cart_items')
      setCartItems(items)
    } catch (error) {
      console.error('Failed to fetch cart:', error)
      setCartItems([])
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchCart()
  }, [user])

  useEffect(() => {
    if (!user && cartItems.length > 0) {
      localStorage.setItem('cart', JSON.stringify(cartItems))
    }
  }, [cartItems, user])

  const addToCart = async (product, quantity = 1) => {
    if (user) {
      // Check if item already exists
      const existing = cartItems.find(item => item.product_id === product.id)
      if (existing) {
        await fetchAPI(`cart_items/${existing.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ quantity: existing.quantity + quantity })
        })
      } else {
        await fetchAPI('cart_items', {
          method: 'POST',
          body: JSON.stringify({
            user_id: user.id,
            product_id: product.id,
            quantity
          })
        })
      }
      fetchCart()
    } else {
      // Local cart for non-authenticated users
      const existing = cartItems.find(item => item.product_id === product.id)
      if (existing) {
        setCartItems(cartItems.map(item => 
          item.product_id === product.id 
            ? { ...item, quantity: item.quantity + quantity }
            : item
        ))
      } else {
        setCartItems([...cartItems, {
          id: `local_${Date.now()}`,
          product_id: product.id,
          product_name: product.name,
          price: product.price,
          quantity,
          image_url: product.image_url
        }])
      }
    }
  }

  const updateQuantity = async (itemId, quantity) => {
    if (itemId.startsWith('local_')) {
      setCartItems(cartItems.map(item => 
        item.id === itemId ? { ...item, quantity } : item
      ))
    } else {
      await fetchAPI(`cart_items/${itemId}`, {
        method: 'PATCH',
        body: JSON.stringify({ quantity })
      })
      fetchCart()
    }
  }

  const removeFromCart = async (itemId) => {
    if (itemId.startsWith('local_')) {
      setCartItems(cartItems.filter(item => item.id !== itemId))
    } else {
      await fetchAPI(`cart_items/${itemId}`, { method: 'DELETE' })
      fetchCart()
    }
  }

  const clearCart = async () => {
    if (user) {
      // Delete all cart items
      for (const item of cartItems) {
        if (!item.id.startsWith('local_')) {
          await fetchAPI(`cart_items/${item.id}`, { method: 'DELETE' })
        }
      }
    }
    setCartItems([])
  }

  const cartTotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0)

  return (
    <CartContext.Provider value={{ 
      cartItems, 
      addToCart, 
      updateQuantity, 
      removeFromCart, 
      clearCart, 
      cartTotal, 
      cartCount,
      loading 
    }}>
      {children}
    </CartContext.Provider>
  )
}

function useCart() {
  return useContext(CartContext)
}

// Header component
function Header() {
  const { user, logout } = useAuth()
  const { cartCount } = useCart()

  return (
    <header className="header">
      <div className="header-content">
        <Link to="/" className="logo">🛒 ShopEasy</Link>
        <nav className="nav">
          <Link to="/" className="nav-link">Home</Link>
          <Link to="/products" className="nav-link">Products</Link>
          <Link to="/categories" className="nav-link">Categories</Link>
          {user ? (
            <>
              <Link to="/orders" className="nav-link">Orders</Link>
              <span className="nav-link">{user.email}</span>
              <button onClick={logout} className="nav-link" style={{ background: 'none', border: 'none' }}>
                Logout
              </button>
            </>
          ) : (
            <Link to="/login" className="nav-link">Login</Link>
          )}
          <Link to="/cart" className="cart-btn">
            🛒
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </Link>
        </nav>
      </div>
    </header>
  )
}

// Home page
function Home() {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const { addToCart } = useCart()
  const [toast, setToast] = useState(null)

  useEffect(() => {
    fetchAPI('products?active=eq.true&limit=8&order=created_at.desc')
      .then(setProducts)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const handleAddToCart = (product) => {
    addToCart(product)
    setToast('Added to cart!')
  }

  return (
    <div>
      <section className="hero">
        <h1>Welcome to ShopEasy</h1>
        <p>Discover amazing products at unbeatable prices</p>
      </section>

      <section className="search-section">
        <div className="search-container">
          <input 
            type="text" 
            placeholder="Search products..." 
            className="search-input"
            id="search-input"
          />
          <button 
            className="search-btn"
            onClick={() => {
              const query = document.getElementById('search-input').value
              window.location.href = `/products?search=${encodeURIComponent(query)}`
            }}
          >
            Search
          </button>
        </div>
      </section>

      <section className="products-section">
        <h2 className="section-title">Featured Products</h2>
        {loading ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : (
          <div className="products-grid">
            {products.map(product => (
              <div key={product.id} className="product-card">
                <img 
                  src={product.image_url || 'https://via.placeholder.com/300x200?text=Product'} 
                  alt={product.name}
                  className="product-image"
                  onError={(e) => e.target.src = 'https://via.placeholder.com/300x200?text=Product'}
                />
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-description">{product.description}</p>
                  <p className="product-price">${(product.price / 100).toFixed(2)}</p>
                  <button 
                    className="add-to-cart-btn"
                    onClick={() => handleAddToCart(product)}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  )
}

// Products page
function Products() {
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const { addToCart } = useCart()
  const [toast, setToast] = useState(null)
  const { user } = useAuth()

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const search = params.get('search') || ''
    setSearchQuery(search)

    Promise.all([
      fetchAPI('products?active=eq.true'),
      fetchAPI('categories')
    ]).then(([productsData, categoriesData]) => {
      setProducts(productsData)
      setCategories(categoriesData)
      setLoading(false)
    }).catch(console.error)
  }, [])

  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchQuery || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase()))
    const matchesCategory = !selectedCategory || product.category_id === selectedCategory
    return matchesSearch && matchesCategory
  })

  const handleAddToCart = (product) => {
    if (!user) {
      setToast('Please login to add items to your cart')
      return
    }
    addToCart(product)
    setToast('Added to cart!')
  }

  return (
    <div>
      <section className="search-section">
        <div className="search-container">
          <input 
            type="text" 
            placeholder="Search products..." 
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button className="search-btn">Search</button>
        </div>
      </section>

      <section className="products-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 className="section-title" style={{ marginBottom: 0 }}>All Products</h2>
          <select 
            value={selectedCategory} 
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #e5e5e5' }}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : (
          <div className="products-grid">
            {filteredProducts.map(product => (
              <div key={product.id} className="product-card">
                <img 
                  src={product.image_url || 'https://via.placeholder.com/300x200?text=Product'} 
                  alt={product.name}
                  className="product-image"
                  onError={(e) => e.target.src = 'https://via.placeholder.com/300x200?text=Product'}
                />
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-description">{product.description}</p>
                  <p className="product-price">${(product.price / 100).toFixed(2)}</p>
                  <button 
                    className="add-to-cart-btn"
                    onClick={() => handleAddToCart(product)}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filteredProducts.length === 0 && (
          <div className="empty-cart">
            <h2>No products found</h2>
            <p>Try a different search term or category</p>
          </div>
        )}
      </section>

      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  )
}

// Categories page
function Categories() {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchAPI('categories')
      .then(setCategories)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  return (
    <section className="categories-section">
      <h2 className="section-title">Shop by Category</h2>
      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div className="categories-grid">
          {categories.map(category => (
            <Link 
              key={category.id} 
              to={`/products?category=${category.id}`}
              className="category-card"
            >
              <h3 className="category-name">{category.name}</h3>
              {category.description && <p style={{ color: '#666', marginTop: '0.5rem' }}>{category.description}</p>}
            </Link>
          ))}
        </div>
      )}
    </section>
  )
}

// Cart page
function Cart() {
  const { cartItems, updateQuantity, removeFromCart, cartTotal } = useCart()
  const navigate = useNavigate()
  const { user } = useAuth()

  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        <div className="empty-cart">
          <h2>Your cart is empty</h2>
          <p>Start shopping to add items to your cart</p>
          <Link to="/products" style={{ color: '#2563eb', fontWeight: '600', marginTop: '1rem', display: 'inline-block' }}>
            Browse Products →
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="cart-page">
      <h2 style={{ marginBottom: '1.5rem' }}>Shopping Cart</h2>
      
      {cartItems.map(item => (
        <div key={item.id} className="cart-item">
          <img 
            src={item.image_url || 'https://via.placeholder.com/80x80?text=Product'} 
            alt={item.product_name}
            className="cart-item-image"
            onError={(e) => e.target.src = 'https://via.placeholder.com/80x80?text=Product'}
          />
          <div className="cart-item-details">
            <h3 className="cart-item-name">{item.product_name}</h3>
            <p className="cart-item-price">${(item.price / 100).toFixed(2)}</p>
          </div>
          <div className="cart-item-quantity">
            <button className="qty-btn" onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}>−</button>
            <span style={{ minWidth: '2rem', textAlign: 'center' }}>{item.quantity}</span>
            <button className="qty-btn" onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
          </div>
          <div className="cart-item-total">
            ${((item.price * item.quantity) / 100).toFixed(2)}
          </div>
          <button className="remove-btn" onClick={() => removeFromCart(item.id)}>🗑️</button>
        </div>
      ))}

      <div className="cart-summary">
        <div className="cart-total">
          <span>Total:</span>
          <span>${(cartTotal / 100).toFixed(2)}</span>
        </div>
        <button 
          className="checkout-btn"
          onClick={() => user ? navigate('/checkout') : navigate('/login')}
        >
          {user ? 'Proceed to Checkout' : 'Login to Checkout'}
        </button>
      </div>
    </div>
  )
}

// Checkout page
function Checkout() {
  const { cartItems, cartTotal, clearCart } = useCart()
  const { user, getToken } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    shipping_name: '',
    shipping_address: '',
    shipping_city: '',
    shipping_zip: ''
  })
  const [toast, setToast] = useState(null)

  if (!user) {
    navigate('/login')
    return null
  }

  if (cartItems.length === 0) {
    navigate('/cart')
    return null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Create order
      const order = await fetchAPI('orders', {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          total: cartTotal,
          status: 'pending',
          ...formData
        })
      })

      // Create order items
      for (const item of cartItems) {
        await fetchAPI('order_items', {
          method: 'POST',
          body: JSON.stringify({
            order_id: order.id,
            product_id: item.product_id,
            product_name: item.product_name,
            price: item.price,
            quantity: item.quantity
          })
        })
      }

      // Clear cart
      await clearCart()

      setToast('Order placed successfully!')
      setTimeout(() => navigate('/orders'), 1500)
    } catch (error) {
      console.error('Checkout error:', error)
      setToast('Failed to place order. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="checkout-page">
      <h2 style={{ marginBottom: '1.5rem' }}>Checkout</h2>
      
      <div className="checkout-form">
        <h3 style={{ marginBottom: '1rem' }}>Shipping Information</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input 
              type="text" 
              className="form-input"
              value={formData.shipping_name}
              onChange={(e) => setFormData({ ...formData, shipping_name: e.target.value })}
              required
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Address</label>
            <input 
              type="text" 
              className="form-input"
              value={formData.shipping_address}
              onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
              required
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">City</label>
              <input 
                type="text" 
                className="form-input"
                value={formData.shipping_city}
                onChange={(e) => setFormData({ ...formData, shipping_city: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">ZIP Code</label>
              <input 
                type="text" 
                className="form-input"
                value={formData.shipping_zip}
                onChange={(e) => setFormData({ ...formData, shipping_zip: e.target.value })}
                required
              />
            </div>
          </div>

          <h3 style={{ margin: '1.5rem 0 1rem' }}>Order Summary</h3>
          {cartItems.map(item => (
            <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e5e5' }}>
              <span>{item.product_name} x {item.quantity}</span>
              <span>${((item.price * item.quantity) / 100).toFixed(2)}</span>
            </div>
          ))}
          
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1.25rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #333' }}>
            <span>Total:</span>
            <span>${(cartTotal / 100).toFixed(2)}</span>
          </div>

          <button 
            type="submit" 
            className="place-order-btn"
            disabled={loading}
            style={{ marginTop: '1.5rem', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Placing Order...' : 'Place Order'}
          </button>
        </form>
      </div>

      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  )
}

// Orders page
function Orders() {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const { user } = useAuth()

  useEffect(() => {
    if (user) {
      fetchAPI('orders?order=created_at.desc')
        .then(setOrders)
        .catch(console.error)
        .finally(() => setLoading(false))
    }
  }, [user])

  if (!user) {
    return (
      <div className="orders-page">
        <div className="empty-cart">
          <h2>Please login to view your orders</h2>
        </div>
      </div>
    )
  }

  return (
    <div className="orders-page">
      <h2 style={{ marginBottom: '1.5rem' }}>Your Orders</h2>
      
      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : orders.length === 0 ? (
        <div className="empty-cart">
          <h2>No orders yet</h2>
          <Link to="/products" style={{ color: '#2563eb', fontWeight: '600' }}>Start shopping →</Link>
        </div>
      ) : (
        orders.map(order => (
          <div key={order.id} className="order-card">
            <div className="order-header">
              <div>
                <p className="order-id">Order #{order.id.slice(0, 8)}</p>
                <p style={{ color: '#666', fontSize: '0.875rem' }}>
                  {new Date(order.created_at).toLocaleDateString()}
                </p>
              </div>
              <span className={`order-status ${order.status}`}>
                {order.status}
              </span>
            </div>
            <div className="order-total">
              Total: ${(order.total / 100).toFixed(2)}
            </div>
          </div>
        ))
      )}
    </div>
  )
}

// Login page
function Login() {
  const { login, register, user } = useAuth()
  const [isLogin, setIsLogin] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  if (user) {
    navigate('/')
    return null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      if (isLogin) {
        await login(email, password)
      } else {
        await register(email, password)
      }
      navigate('/')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-form">
        <h2 className="auth-title">{isLogin ? 'Login' : 'Create Account'}</h2>
        
        {error && (
          <div style={{ background: '#fee2e2', color: '#dc2626', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input 
              type="email" 
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Password</label>
            <input 
              type="password" 
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>

          <button type="submit" className="auth-btn" disabled={loading}>
            {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Sign Up')}
          </button>
        </form>

        <p className="auth-link">
          {isLogin ? (
            <>Don't have an account? <a href="#" onClick={() => setIsLogin(false)}>Sign up</a></>
          ) : (
            <>Already have an account? <a href="#" onClick={() => setIsLogin(true)}>Login</a></>
          )}
        </p>
      </div>
    </div>
  )
}

// Main App component
function App() {
  return (
    <Router>
      <AuthProvider>
        <CartProvider>
          <div className="app">
            <Header />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/products" element={<Products />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/login" element={<Login />} />
              </Routes>
            </main>
          </div>
        </CartProvider>
      </AuthProvider>
    </Router>
  )
}

export default App